"use client";

import React, { useCallback, useEffect } from "react";
import styled, { css } from "styled-components";
import { Icon, Box, Text, useNavigation, Flex, Button } from "@thenounproject/lingo-core";

import useNotifications, { Level, Notification } from "@actions/useNotifications";
import { useAppSelectorV1 } from "@redux/hooks";
import { getColor, getShadow } from "../../../core/src/components/utils";

type UseNavigation = ReturnType<typeof useNavigation>;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

type TimerInfo = { timerId: ReturnType<typeof setTimeout>; remaining: number; started: number };

/** Auto-dismiss notifications with pause-on-hover support. */
function useAutoDismiss(
  notifications: Notification[],
  isHovered: boolean,
  dismiss: (id: string) => void
) {
  const timersRef = React.useRef<Map<string, TimerInfo>>(new Map());

  useEffect(() => {
    notifications.forEach(n => {
      if (timersRef.current.has(n.id)) return;
      const timeout = n.action ? 10000 : n.link ? 8000 : 5000;
      if (isHovered) {
        timersRef.current.set(n.id, { timerId: 0 as never, remaining: timeout, started: 0 });
      } else {
        const timerId = setTimeout(() => {
          timersRef.current.delete(n.id);
          dismiss(n.id);
        }, timeout);
        timersRef.current.set(n.id, { timerId, remaining: timeout, started: Date.now() });
      }
    });

    const currentIds = new Set(notifications.map(n => n.id));
    timersRef.current.forEach((info, id) => {
      if (!currentIds.has(id)) {
        clearTimeout(info.timerId);
        timersRef.current.delete(id);
      }
    });
  }, [notifications, dismiss, isHovered]);

  useEffect(() => {
    if (isHovered) {
      timersRef.current.forEach((info, id) => {
        clearTimeout(info.timerId);
        const elapsed = info.started ? Date.now() - info.started : 0;
        timersRef.current.set(id, {
          ...info,
          remaining: Math.max(info.remaining - elapsed, 500),
          started: 0,
        });
      });
    } else {
      timersRef.current.forEach((info, id) => {
        if (info.started !== 0) return;
        const timerId = setTimeout(() => {
          timersRef.current.delete(id);
          dismiss(id);
        }, info.remaining);
        timersRef.current.set(id, { timerId, remaining: info.remaining, started: Date.now() });
      });
    }
  }, [isHovered, dismiss]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timersRef.current.forEach(info => clearTimeout(info.timerId));
    };
  }, []);
}

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const NotificationContainer = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-left: -204px;
  width: 408px;
  position: relative;
`;

const DURATION = 400,
  EASING = "cubic-bezier(0.4, 0, 0.2, 1)";

// -- Slot: controls space & visibility --------------------------------------

type SlotProps = {
  $mounted: boolean;
  $exiting: boolean;
  $i: number;
  $n: number;
  $hovered: boolean;
};

const NotificationSlot = styled.li<SlotProps>`
  list-style: none;
  position: relative;

  ${({ $mounted, $exiting, $i, $n, $hovered }) => {
    const expanded = $mounted && ($hovered || $i === 0) && !$exiting,
      maxHeight = !$mounted || $exiting ? 0 : expanded ? 250 : Math.max(0, 18 - $i * 5),
      marginBottom = !$mounted || $exiting ? 0 : expanded ? 16 : 0,
      opacity = !$mounted || $exiting ? 0 : expanded ? 1 : Math.max(0, 1 - $i * 0.3);

    const t = `${DURATION}ms ${EASING}`;

    return css`
      z-index: ${$n - $i};
      max-height: ${maxHeight}px;
      margin-bottom: ${marginBottom}px;
      opacity: ${opacity};
      pointer-events: ${expanded ? "auto" : "none"};
      transition:
        max-height ${t},
        margin-bottom ${t},
        opacity ${t};
    `;
  }}
`;

// -- Card: visual cascade appearance ----------------------------------------

type CardProps = {
  level: Level;
  $i: number;
  $hovered: boolean;
  $mounted: boolean;
};

const NotificationCard = styled(Box).attrs<CardProps>(props => ({
  boxShadow: "panel",
  borderRadius: "default",
  background: props.level === "error" ? "error" : "info",
  textAlign: "left",
  position: "relative",
}))<CardProps>`
  transform-origin: bottom center;
  transition: transform ${DURATION}ms ${EASING};

  & .inner {
    padding: 8px 16px;
  }
  & p {
    margin: 0;
  }

  ${({ $i, $hovered, $mounted }) => {
    const expanded = !$mounted || $hovered || $i === 0;
    return css`
      transform: scale(${expanded ? 1 : Math.max(0.85, 1 - $i * 0.05)});
    `;
  }}
  .close-button {
    opacity: 0;
    transition: opacity 0.2s;
  }
  &:hover {
    .close-button {
      opacity: 1;
    }
  }
`;

const CloseButton = styled.button<ButtonProps>`
  position: absolute;
  top: -4px;
  left: -4px;
  padding: 0;
  height: 16px;
  width: 16px;
  background-color: ${props => getColor(props.level === "error" ? "errorDark" : "infoDark")};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: ${getShadow("button")};
`;

const NotificationLinkText = styled(Text).attrs({ as: "span", font: "ui.small" })`
  color: white !important;
  text-decoration: underline;
  font-weight: bold;
`;

type ButtonProps = { level: Level };
const NotificationActionButton = styled(Button).attrs<ButtonProps>(props => ({
  themeOverrides: {
    primaryColor: props.level === "error" ? "error" : "infoDark",
    primaryColorDark: props.level === "error" ? "errorDark" : "infoDark",
    primaryColorTint: "white",
  },
  size: "small",
  buttonStyle: "primary",
  flexShrink: 0,
}))<ButtonProps>`
  border: 1px solid ${props => getColor(props.level === "error" ? "errorDark" : "infoDark")};
  cursor: pointer;
  margin-left: 4px;
`;

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

type NotificationContentProps = {
  notification: Notification;
  navigation: UseNavigation;
  dismiss: (id: string) => void;
};
const NotificationContent: React.FC<NotificationContentProps> = ({
  notification,
  navigation,
  dismiss,
}) => {
  const { link, message, action, level } = notification;

  const onDismiss = useCallback(() => {
    dismiss(notification.id);
  }, [dismiss, notification.id]);

  function renderMessage() {
    if (Array.isArray(message)) {
      return message.map(comp => {
        if (typeof comp === "string") return comp;
        return (
          <button
            key={comp.text}
            style={{ wordBreak: "break-all" }}
            onClick={e => {
              e.preventDefault();
              if (comp.url) navigation.push(comp.url);
              else comp?.onClick();
              dismiss(notification.id);
            }}>
            <NotificationLinkText>{comp.text}</NotificationLinkText>
          </button>
        );
      });
    }
    return String(notification.message);
  }

  return (
    <div className="inner">
      <CloseButton level={level} className="close-button" onClick={onDismiss}>
        <Icon iconId="action.close" fill="white" size={14} />
      </CloseButton>
      <div>
        <Flex width="100%" alignItems="center" justifyContent="space-between">
          <Text as="span" color="white" font="ui.small" style={{ wordBreak: "break-word" }}>
            {renderMessage()}
            {link && (
              <button
                key={link.text}
                style={{ wordBreak: "break-all" }}
                onClick={e => {
                  e.preventDefault();
                  navigation.push(link.url);
                  onDismiss();
                }}>
                <NotificationLinkText>{link.text}</NotificationLinkText>
              </button>
            )}
          </Text>
          {action && (
            <NotificationActionButton
              level={notification.level}
              onClick={() => {
                void action.onClick();
                dismiss(notification.id);
              }}
              text={action.text}></NotificationActionButton>
          )}
        </Flex>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type Notifications = {
  [key: string]: Notification;
};

const Notifications = () => {
  const navigation = useNavigation();
  const notifications = useAppSelectorV1(state => state.notifications);
  const { dismissNotification } = useNotifications();
  const [isHovered, setIsHovered] = React.useState(false);
  const [mountedIds, setMountedIds] = React.useState<Set<string>>(new Set());
  const [exitingIds, setExitingIds] = React.useState<Set<string>>(new Set());

  // Mount new notifications after one frame so the browser paints initial (0) values,
  // then transitions fire to the computed targets.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setMountedIds(prev => {
        const next = new Set(prev);
        let changed = false;
        notifications.forEach(n => {
          if (!next.has(n.id)) {
            next.add(n.id);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [notifications]);

  // Clean up mountedIds when notifications are removed
  useEffect(() => {
    const currentIds = new Set(notifications.map(n => n.id));
    setMountedIds(prev => {
      const next = new Set([...prev].filter(id => currentIds.has(id)));
      return next.size !== prev.size ? next : prev;
    });
  }, [notifications]);

  const beginDismiss = React.useCallback(
    (id: string) => {
      setExitingIds(prev => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setTimeout(() => {
        setExitingIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setMountedIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        dismissNotification(id);
      }, DURATION + 50);
    },
    [dismissNotification]
  );

  useAutoDismiss(notifications, isHovered, beginDismiss);

  if (notifications.length === 0) return null;

  return (
    <NotificationContainer
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      {notifications.map((notification, index) => {
        const i = notifications.length - 1 - index,
          mounted = mountedIds.has(notification.id),
          exiting = exitingIds.has(notification.id);
        return (
          <NotificationSlot
            key={notification.id}
            $i={i}
            $n={notifications.length}
            $hovered={isHovered}
            $mounted={mounted}
            $exiting={exiting}>
            <NotificationCard
              level={notification.level}
              role="status"
              $i={i}
              $hovered={isHovered}
              $mounted={mounted}>
              <NotificationContent
                notification={notification}
                dismiss={beginDismiss}
                navigation={navigation}
              />
            </NotificationCard>
          </NotificationSlot>
        );
      })}
    </NotificationContainer>
  );
};

export default Notifications;
